import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Toolbar, Sort, Edit, Inject, Filter, Selection } from '@syncfusion/ej2-react-grids';
import { useSearchParams } from 'react-router-dom';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import {
  TabComponent,
  TabItemDirective,
  TabItemsDirective
} from "@syncfusion/ej2-react-navigations";

import { message } from 'antd';
import { useTranslation } from '../../i18n';

import withMosadIsolation from '../../utils/withMosadIsolation';
import {
  createCustomer,
  updateCustomer
} from '../../redux/slices/customersSlice';
import styles from './Customers.module.css';

const Customers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { banks } = useSelector(state => state.banks);
  const {
    customersList
  } = useSelector(state => state.customers);
  const [searchParams] = useSearchParams();
  const selectedNumberParam = searchParams.get('number');
  const appliedSelectionRef = React.useRef(null);

  const filterSettings = { type: 'Excel' };
  const toolbarOptions = ['Add', 'Edit'];
  const editSettings = { allowEditing: true, allowAdding: true, allowDeleting: false, mode: 'Dialog', template: dialogTemplate };
  // const orderidRules = { required: true, number: true };
  const pageSettings = { pageCount: 5 };
  const gridRef = React.useRef(null);

  function dialogTemplate(props) {
    return (<DialogFormTemplate {...props} />);
  }

  function formatDateDdMmYyyy(value) {
    if (value === null || value === undefined || value === '') return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function dateValueAccessor(field, data) {
    return formatDateDdMmYyyy(data?.[field]);
  }

  function getSuggestedCustomerNumber() {
    const numbers = (customersList || [])
      .map((c) => Number(c.number))
      .filter((n) => Number.isInteger(n) && n > 0);
    const maxNumber = numbers.length ? Math.max(...numbers) : 0;
    return maxNumber + 1;
  }

  function parseCustExtern(value) {
    if (value === undefined || value === null || value === '') return 0;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.trunc(n);
  }

  function readDialogField(args, fieldId) {
    const el = args.dialog?.element?.querySelector?.(`#${fieldId}`) || document.getElementById(fieldId);
    if (!el) return undefined;
    return el.value;
  }

  function extractErrorMessage(err) {
    if (!err) return t('notifications.frontend.common.unknownError');
    if (typeof err === 'string') return err;
    if (Array.isArray(err?.message)) {
      return err.message.map(e => (typeof e === 'string' ? e : (e?.msg || e?.message || String(e)))).join(', ');
    }
    if (Array.isArray(err)) {
      return err.map(e => (typeof e === 'string' ? e : (e?.msg || e?.message || String(e)))).join(', ');
    }
    if (typeof err?.message === 'string') return err.message;
    if (typeof err?.error === 'string') return err.error;
    if (Array.isArray(err?.error)) {
      return err.error.map(e => (typeof e === 'string' ? e : (e?.msg || e?.message || String(e)))).join(', ');
    }
    if (typeof err?.msg === 'string') return err.msg;
    return t('notifications.frontend.common.unknownError');
  }

  async function actionBegin(args) {
    if (args.requestType === 'save') {
      args.cancel = true; // prevent Grid from inserting/updating locally

      const paidPayments = args.data?.paid_payments;
      if (paidPayments != null && (paidPayments < 0 || paidPayments > 1000)) {
        message.error(t('ui.customers.validationPaidPaymentsRange', { min: 0, max: 1000 }) || 'Paid payments must be between 0 and 1000', 3);
        return;
      }
      const paymentsNumber = Number(args.data?.payments_number);
      if (args.data?.payments_number != null && !((paymentsNumber === -1) || (Number.isInteger(paymentsNumber) && paymentsNumber >= 1 && paymentsNumber <= 1000))) {
        message.error(t('ui.customers.validationPaymentsNumberRange', { min: 1, max: 1000 }) || 'Payments number must be -1 or between 1 and 1000', 3);
        return;
      }
      const chargeDate = Number(args.data?.charge_date);
      if (args.data?.charge_date != null && (!Number.isInteger(chargeDate) || chargeDate < 1 || chargeDate > 31)) {
        message.error(t('ui.customers.validationChargeDateRange', { min: 1, max: 31 }) || 'Charge date must be between 1 and 31', 3);
        return;
      }
      const rawExtern = args.data?.cust_extern ?? readDialogField(args, 'cust_extern');
      const custExtern = parseCustExtern(rawExtern);
      if (custExtern === null) {
        message.error(t('ui.customers.validationExternNumber') || 'External number must be a non-negative integer', 3);
        return;
      }
      args.data = { ...args.data, cust_extern: custExtern };
      const newCustomerNumber = Number(args.data?.number);
      if (args.action === 'add' && Number.isInteger(newCustomerNumber) && newCustomerNumber > 0) {
        if ((customersList || []).some((c) => Number(c.number) === newCustomerNumber)) {
          message.error(`Customer number ${newCustomerNumber} already exists for this mosad`, 3);
          return;
        }
      }

      try {
        if (args.action === 'add') {
          await dispatch(createCustomer({ ...args.data })).unwrap();
          message.success(t('notifications.frontend.common.customerCreatedSuccessfully'));
        } else if (args.action === 'edit') {
          await dispatch(updateCustomer({ ...args.data })).unwrap();
          message.success(t('notifications.frontend.common.customerUpdatedSuccessfully'));
        }
        // reload from DB so grid only shows persisted data
        // await dispatch(fetchCustomers());
        // Properly end edit via Grid API (avoid manually closing dialog)
        gridRef.current && gridRef.current.closeEdit();
        args.dialog.close();
        args.dialog.hide();
      } catch (err) {
        const errMsg = extractErrorMessage(err);
        message.error(t('notifications.frontend.common.failedToSaveCustomer', { error: errMsg }), 3);
      }
    }
  }

  async function actionComplete(args) {
    if ((args.requestType === 'beginEdit' || args.requestType === 'add')) {
      // Set dialog properties
      args.dialog.width = '30%';
      args.dialog.height = '70%';
      args.dialog.animationSettings = { effect: 'FadeZoom', duration: 300 };
      args.dialog.showCloseIcon = true;
      args.dialog.closeOnEscape = true;
      args.dialog.allowDragging = true;

      // Move popup to top of the page
      args.dialog.position = { X: 'center', Y: 100 }; // 100px from top

      args.dialog.dataBind();
    }
    // No-op for 'save' here; handled in actionBegin
  }

  function handleDataBound() {
    const grid = gridRef.current;
    if (!grid || !selectedNumberParam) return;

    const customer = (customersList || []).find(
      (c) => String(c.number) === String(selectedNumberParam)
    );
    if (!customer) return;

    if (appliedSelectionRef.current !== String(selectedNumberParam)) {
      appliedSelectionRef.current = String(selectedNumberParam);
      setTimeout(() => {
        grid.filterByColumn('number', 'equal', customer.number);
      }, 0);
      return;
    }

    const view = grid.getCurrentViewRecords?.() || [];
    const idx = view.findIndex((r) => String(r.number) === String(selectedNumberParam));
    if (idx >= 0) {
      const selected = grid.getSelectedRecords?.() || [];
      if (!selected.length || String(selected[0].number) !== String(selectedNumberParam)) {
        grid.selectRow(idx);
      }
    }
  }

  return (<div className='control-pane'>
    <div className='control-section'>
      <h2>{t('ui.customers.title')}</h2>
      <div style={{ width: '100%', overflowX: 'auto' }}>
      <GridComponent ref={gridRef} dataSource={(customersList || []).map(item => ({ ...item, charge_sum: Number(item.charge_sum) || 0, cust_extern: Number(item.cust_extern) || 0 }))} width="100%" toolbar={toolbarOptions} allowSorting={true} allowFiltering={true} filterSettings={filterSettings} allowPaging={true} editSettings={editSettings} pageSettings={pageSettings} enableRtl={true} actionBegin={actionBegin} actionComplete={actionComplete.bind(this)} dataBound={handleDataBound} selectionSettings={{ type: 'Single', mode: 'Row' }}>
        <ColumnsDirective>
          <ColumnDirective field='number' headerText={t('ui.customers.number')} width='80' textAlign="Center" isPrimaryKey={true} />
          <ColumnDirective field='cust_extern' headerText={t('ui.customers.externNumber')} width='110' textAlign="Center" type="number" />
          <ColumnDirective field='name' headerText={t('ui.customers.name')} width='150' textAlign="Center" />
          <ColumnDirective field='id' headerText={t('ui.customers.id')} width='120' textAlign="Center" />
          <ColumnDirective field='charge_sum' headerText={t('ui.customers.chargeSum')} width='120' format="C2" textAlign="Center" type="number" />
          <ColumnDirective field='charge_date' headerText={t('ui.customers.chargeDay')} width='100' textAlign="Center" />
          {/* <ColumnDirective field='charge_cycle' headerText='Charge Cycle' width='100' textAlign="Center" /> */}
          <ColumnDirective field='expired_date' headerText={t('ui.customers.expiredDate')} width='120' textAlign="Center" valueAccessor={dateValueAccessor} />
          {/* <ColumnDirective field='paid_payments' headerText='Paid Payments' width='100' textAlign="Center"/> */}
          {/* <ColumnDirective field='payments_number' headerText='Payments Number' width='120' textAlign="Center"/> */}
        </ColumnsDirective>
        <Inject services={[Page, Toolbar, Edit, Sort, Filter, Selection]} />
      </GridComponent>
      </div>

    </div>
  </div>);

  function DialogFormTemplate(props) {
    const formDataRef = React.useRef({ ...props });

    function onChange(e) {
      let key, value;
      if (e?.target) {
        key = e.target.name;
        value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      } else if (e?.element) {
        key = e.element.id;
        value = e.value;
      }
      if (key) {
        formDataRef.current[key] = value;
      }
    }

    const personalTemplate = () => {
      const suggestedCustomerNumber = props.isAdd ? getSuggestedCustomerNumber() : props.number;      
      return (
      <div>
        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="number"
                name="number"
                type="number"
                min="1"
                step="1"
                disabled={!props.isAdd}
                defaultValue={suggestedCustomerNumber}
                onChange={onChange}
                required={true}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.number')}</label>
            </div>
          </div>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="cust_extern"
                name="cust_extern"
                type="number"
                min="0"
                step="1"
                defaultValue={props.cust_extern ?? 0}
                onChange={onChange}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.externNumber')}</label>
            </div>
          </div>
        </div>

        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="name"
                name="name"
                type="text"
                defaultValue={props.name}
                onChange={onChange}
                required={true}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.name')}</label>
            </div>
          </div>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="id"
                name="id"
                type="text"
                defaultValue={props.id}
                onChange={onChange}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.id')}</label>
            </div>
          </div>
        </div>

        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="phone"
                name="phone"
                type="text"
                defaultValue={props.phone}
                onChange={onChange}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.phone')}</label>
            </div>
          </div>
        </div>

        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="email"
                name="email"
                type="email"
                defaultValue={props.email}
                onChange={onChange}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.email')}</label>
            </div>
          </div>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="address"
                name="address"
                defaultValue={props.address}
                onChange={onChange}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.address')}</label>
            </div>
          </div>
        </div>

        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-12"]}`}>
            <div className="e-float-input e-control-wrapper">
              <textarea
                className={styles["input-group"]}
                id="notes"
                name="notes"
                defaultValue={props.notes}
                onChange={onChange}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.notes')}</label>
            </div>
          </div>
        </div>
      </div>
    );
  };

    const paymentTemplate = () => {
       const expiredDateValue = props.expired_date ?? (props.isAdd ? '31/12/2099' : null);
       return(
      <div>
        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <DatePickerComponent
              className={styles["input-group"]}
              id="expired_date"
              name="expired_date"
              value={expiredDateValue}
              format="dd/MM/yyyy"
              placeholder={t('ui.customers.expiredDate')}
              floatLabelType="Always"
              change={onChange}
              required={true}
            />
          </div>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <NumericTextBoxComponent
              className={styles["input-group"]}
              id="charge_sum"
              name="charge_sum"
              format="₪ ##,###.##"
              value={props.charge_sum}
              placeholder={t('ui.customers.chargeSum')}
              floatLabelType="Always"
              change={onChange}
              required={true}
            />
          </div>
        </div>

        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <NumericTextBoxComponent
              className={styles["input-group"]}
              id="charge_date"
              name="charge_date"
              format="##"
              min={1}
              max={31}
              value={props.charge_date}
              placeholder={t('ui.customers.chargeDay')}
              floatLabelType="Always"
              change={onChange}
              required={true}
            />
          </div>
          {/* <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <NumericTextBoxComponent
              className={styles["input-group"]}
              id="charge_cycle"
              name="charge_cycle"
              format="##"
              value={props.charge_cycle}
              placeholder="Charge Day"
              floatLabelType="Always"
              change={onChange}
              required={true}
            />
          </div> */}
        </div>

        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <NumericTextBoxComponent
              className={styles["input-group"]}
              id="paid_payments"
              name="paid_payments"
              format="##"
              min={0}
              max={1000}
              value={props.paid_payments}
              placeholder={t('ui.customers.paidPayments')}
              floatLabelType="Always"
              change={onChange}
              required={true}
            />
          </div>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <NumericTextBoxComponent
              className={styles["input-group"]}
              id="payments_number"
              name="payments_number"
              format="##"
              min={-1}
              max={1000}
              value={props.payments_number}
              placeholder={t('ui.customers.paymentsNumber')}
              floatLabelType="Always"
              change={onChange}
              required={true}
            />
          </div>
        </div>

        <div className={styles["form-row"]}>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="code1"
                name="code1"
                format="##"
                defaultValue={props.code1}
                onChange={onChange}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.code1')}</label>
            </div>
          </div>
          <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
            <div className="e-float-input e-control-wrapper">
              <input
                className={styles["input-group"]}
                id="code2"
                name="code2"
                format="##"
                defaultValue={props.code2}
                onChange={onChange}
              />
              <span className="e-float-line"></span>
              <label className="e-float-text e-label-top">{t('ui.customers.code2')}</label>
            </div>
          </div> 
        </div>
      </div>
    );
  };

    const bankTemplate = () => {
      // const bankCodeName = banks.map(bank => ({
      //   ...bank,
      //   displayName: `${bank.code} - ${bank.name}`
      // }));

      // let bankDisplayName = '';
      // // If bank code exists and not null, find matching bank
      // if (props.bank_code) {
      //   const matchedBank = banks.find(bank => parseInt(bank.code) === parseInt(props.bank_code));
      //   if (matchedBank) {
      //     // Update bank displayName to match the code
      //     bankDisplayName = matchedBank.code + ' - ' + matchedBank.name;
      //   }
      // }

      const bankOptions = (banks || []).map(b => ({
        code: String(b.code),
        displayName: `${b.code} - ${b.name}`,
      }));

      const selectedValue =
        props.cust_bank_code != null
          ? String(props.cust_bank_code)
          : (props.bank_code != null ? String(props.bank_code) : null);


      return (
        <div>
          <div className={styles["form-row"]}>

            <div className={`${styles["form-group"]} ${styles["col-md-12"]}`}>
              <DropDownListComponent
                id="cust_bank_code"
                name="cust_bank_code"
                className={styles["input-group"]}
                key={(bankOptions.length ? 'banks-ready' : 'banks-empty') + '-' + (selectedValue ?? 'none')}
                dataSource={bankOptions}
                fields={{ text: 'displayName', value: 'code' }}
                value={bankOptions.length ? selectedValue : null}  // only set after options exist
                placeholder={t('ui.customers.bank')}
                popupHeight="300px"
                floatLabelType="Always"
                change={onChange}
              />

              {/* <DropDownListComponent
                className={styles["input-group"]}
                value={bankDisplayName}
                dataSource={bankCodeName}
                fields={{ text: "displayName", value: "displayName" }}
                placeholder="Code"
                popupHeight="300px"
                floatLabelType="Always"
                change={onChange}
                required={true}
              /> */}
            </div>
          </div>

          <div className={styles["form-row"]}>
            <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
              <div className="e-float-input e-control-wrapper">
                <input
                  className={styles["input-group"]}
                  id="bank_branch"
                  name="bank_branch"
                  defaultValue={props.bank_branch}
                  onChange={onChange}
                  required={true}
                />
                <span className="e-float-line"></span>
                <label className="e-float-text e-label-top">{t('ui.customers.branch')}</label>
              </div>
            </div>
            <div className={`${styles["form-group"]} ${styles["col-md-6"]}`}>
              <div className="e-float-input e-control-wrapper">
                <input
                  className={styles["input-group"]}
                  id="bank_account"
                  name="bank_account"
                  defaultValue={props.bank_account}
                  onChange={onChange}
                  required={true}
                />
                <span className="e-float-line"></span>
                <label className="e-float-text e-label-top">{t('ui.customers.account')}</label>
              </div>
            </div>
          </div>

          <div className={styles["form-row"]}>
            <div className={`${styles["form-group"]} ${styles["col-md-4"]}`}>
              <div className="e-float-input e-control-wrapper">
                <input
                  id="isActive"
                  name="isActive"
                  type="checkbox"
                  defaultChecked={props.isActive || props.isAdd}
                  onChange={onChange}
                />
                <span className="e-float-line"></span>
                <label className="e-float-text e-label-top">{t('ui.customers.isActive')}</label>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div>
        <TabComponent>
          <TabItemsDirective>
            <TabItemDirective header={{ text: t('ui.customers.personalTab') }} content={personalTemplate} />
            <TabItemDirective header={{ text: t('ui.customers.paymentTab') }} content={paymentTemplate} />
            <TabItemDirective header={{ text: t('ui.customers.bankTab') }} content={bankTemplate} />
          </TabItemsDirective>
        </TabComponent>
      </div>
    );
  }

}

export default withMosadIsolation(Customers);